import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, setHeaderMenuStyle, unlockScroll } from '@theme/utilities';

/**
 * @typedef {object} NavigationRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} bar
 * @property {HTMLAnchorElement[]} [trigger]
 * @property {HTMLButtonElement[]} [switcher]
 * @property {HTMLElement[]} [panel]
 *
 * Native modal navigation owns focus containment and Escape behavior. On touch
 * devices and narrow screens, the header continues to use Horizon's drawer.
 * @extends {Component<NavigationRefs>}
 */
class HeaderNavigationPanel extends Component {
  requiredRefs = ['dialog', 'bar'];
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {HTMLDialogElement | undefined} */
  #dialog;
  /** @type {HTMLAnchorElement | undefined} */
  #opener;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#bindDialog();
    setHeaderMenuStyle();
  }

  updatedCallback() {
    super.updatedCallback();
    // Close and rebind even if only the dialog's contents were replaced.
    this.#bindDialog();
  }

  disconnectedCallback() {
    this.#releaseDialog();
    super.disconnectedCallback();
  }

  #bindDialog() {
    this.#releaseDialog();
    this.#dialog = this.refs.dialog;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#dialog.addEventListener('close', this.#afterClose, { signal });
    this.#dialog.addEventListener('click', this.#outsideClick, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#dialog?.open) this.#alignPanel();
    });
    this.#resizeObserver.observe(this.refs.bar);
    const header = this.closest('#header-component');
    if (header) this.#resizeObserver.observe(header);
    // Reset stale expanded state after a section response or reconnect.
    this.#resetTriggers();
  }

  #releaseDialog() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    if (this.#dialog) {
      if (this.#dialog.open) this.#dialog.close();
      unlockScroll(this.#dialog);
    }
    this.#resetTriggers();
    this.#opener = undefined;
  }

  #resetTriggers() {
    for (const trigger of this.refs.trigger ?? []) trigger.setAttribute('aria-expanded', 'false');
  }

  /** Open only unmodified primary clicks; links remain usable before enhancement.
   * @param {MouseEvent} event
   */
  openPanel(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target;
    if (!(trigger instanceof HTMLAnchorElement) || !this.#select(trigger.dataset.panel)) return;
    event.preventDefault();
    this.#opener = trigger;
    this.#alignPanel();
    this.refs.dialog.showModal();
    lockScroll(this.refs.dialog);
    trigger.setAttribute('aria-expanded', 'true');
  }

  // Read the real header geometry so font settings, rows, and RTL stay aligned.
  #alignPanel() {
    const bar = this.refs.bar.getBoundingClientRect();
    const header = this.closest('#header-component')?.getBoundingClientRect();
    const inlineStart = getComputedStyle(this).direction === 'rtl' ? window.innerWidth - bar.right : bar.left;
    const style = this.refs.dialog.style;
    style.setProperty('--vo2-nav-origin-inline', `${inlineStart}px`);
    style.setProperty('--vo2-nav-origin-block', `${bar.top}px`);
    style.setProperty('--vo2-nav-bar-height', `${bar.height}px`);
    style.setProperty('--vo2-nav-header-end', `${Math.max(bar.bottom, header?.bottom ?? 0)}px`);
  }

  /** @param {MouseEvent} event */
  selectPanel(event) {
    const button = event.target;
    if (button instanceof HTMLButtonElement) this.#select(button.dataset.panel);
  }

  /** @param {string | undefined} id */
  #select(id) {
    const panels = this.refs.panel ?? [];
    if (!panels.some((panel) => panel.dataset.panel === id)) return false;
    for (const panel of panels) panel.hidden = panel.dataset.panel !== id;
    for (const button of this.refs.switcher ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.panel === id));
    }
    return true;
  }

  /** Close buttons and destination links share this handler; link navigation is native. */
  closePanel() {
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#afterClose();
  }

  #afterClose = () => {
    if (this.#dialog?.open) return;
    if (this.#dialog) unlockScroll(this.#dialog);
    this.#resetTriggers();
    // Native Escape also fires close, returning focus to the invoking link.
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.#opener = undefined;
  };

  /** @param {MouseEvent} event */
  #outsideClick = (event) => {
    if (this.#dialog && isClickedOutside(event, this.#dialog)) this.closePanel();
  };

  #onResize = () => {
    setHeaderMenuStyle();
    if (window.innerWidth < 750 || this.closest('#header-component')?.dataset.menuStyle === 'drawer') {
      this.closePanel();
    } else if (this.#dialog?.open) {
      this.#alignPanel();
    }
  };
}

if (!customElements.get('header-navigation-panel')) {
  customElements.define('header-navigation-panel', HeaderNavigationPanel);
}
